import { useLayoutEffect, useMemo, useRef, useState } from "react";
import "./SalesExplorer.css";

const DIMENSIONS = [
  { key: "route", label: "Route", plural: "routes" },
  { key: "customer", label: "Customer", plural: "customers" },
  { key: "vendor", label: "Vendor", plural: "vendors" },
  { key: "product_category", label: "Product category", plural: "product categories", search: "categories" },
  { key: "account_type", label: "Account type", plural: "account types" },
  { key: "salesman", label: "Salesman / office", plural: "salesmen / offices", search: "salespeople" },
];
const COLORS = [
  { background: "#214989", foreground: "#fff" },
  { background: "#6379fa", foreground: "#fff" },
  { background: "#aaa9f4", foreground: "#101b43" },
  { background: "#f7c952", foreground: "#101b43" },
  { background: "#7b8ba9", foreground: "#101b43" },
];
const OTHER_COLOR = { background: "#a1adc3", foreground: "#101b43" };
const EMPTY_ROWS = [];
const currency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 });
const compactCurrency = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", notation: "compact", maximumFractionDigits: 1 });
const number = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};
const money = (value, signed = false) => {
  const amount = number(value);
  return `${amount < 0 ? "−" : signed && amount > 0 ? "+" : ""}${currency.format(Math.abs(amount))}`;
};
const percent = (value) => {
  if (value == null) return "No baseline";
  const amount = number(value);
  return `${amount < 0 ? "−" : amount > 0 ? "+" : ""}${Math.abs(amount).toFixed(2)}%`;
};
const share = (value) => value == null ? "—" : `${number(value).toFixed(2)}%`;
const date = (value) => value ? new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" }).format(new Date(`${value}T00:00:00Z`)) : "";
const range = (value) => value?.start_date && value?.end_date ? `${date(value.start_date)} – ${date(value.end_date)}` : "";
const keyOf = (row) => String(row.key ?? row.id ?? row.name);
const nameOf = (row) => row.name || "Unassigned";
const toneOf = (value) => number(value) > 0 ? "gain" : number(value) < 0 ? "decline" : "flat";

function mixRows(rows, hasComparison) {
  const ranked = [...rows].filter((row) => number(row.sales) > 0 || (hasComparison && number(row.previous_sales) > 0))
    .sort((a, b) => Math.max(number(b.sales), hasComparison ? number(b.previous_sales) : 0) - Math.max(number(a.sales), hasComparison ? number(a.previous_sales) : 0));
  const leaders = ranked.slice(0, 5).map((row, index) => ({ ...row, chartKey: keyOf(row), color: COLORS[index] }));
  if (ranked.length > 5) {
    leaders.push({ chartKey: "other", name: "Other", color: OTHER_COLOR,
      sales: ranked.slice(5).reduce((sum, row) => sum + Math.max(0, number(row.sales)), 0),
      previous_sales: ranked.slice(5).reduce((sum, row) => sum + Math.max(0, number(row.previous_sales)), 0) });
  }
  return leaders;
}

function MixBar({ title, dates, field, entries, total }) {
  const positiveTotal = entries.reduce((sum, row) => sum + Math.max(0, number(row[field])), 0);
  return <div className="sales-explorer-period">
    <div className="sales-explorer-period-label"><strong>{title}</strong><span>{dates}</span></div>
    <div className="sales-explorer-track" role="img" aria-label={`${title} positive sales mix`}>
      {positiveTotal > 0 ? entries.map((row) => {
        const value = Math.max(0, number(row[field]));
        const mixPercent = value / positiveTotal * 100;
        return value > 0 && <div key={row.chartKey} className="sales-explorer-segment" style={{ width: `${mixPercent}%`, backgroundColor: row.color.background, color: row.color.foreground }} title={`${nameOf(row)}: ${money(value)}`} aria-label={`${nameOf(row)} ${money(value)}`}>{mixPercent >= 8 && <span>{compactCurrency.format(value)}{mixPercent >= 12 && <small>({mixPercent.toFixed(1)}%)</small>}</span>}</div>;
      }) : <span className="sales-explorer-no-mix">No positive sales to chart</span>}
    </div>
    <strong className="sales-explorer-period-total">{money(total)}</strong>
  </div>;
}

function SortHeader({ field, label, activeSort, sortDirection, onSort }) {
  const active = activeSort === field;
  const ariaSort = active ? (sortDirection === "asc" ? "ascending" : "descending") : "none";
  return <th scope="col" aria-sort={ariaSort}>
    <button type="button" className="sales-explorer-sort-button" aria-label={`Sort by ${label}`} onClick={() => onSort(field)}>
      <span>{label}</span><span className="sales-explorer-sort-icon" aria-hidden="true">{active ? sortDirection === "asc" ? "↑" : "↓" : "↕"}</span>
    </button>
  </th>;
}

export default function SalesExplorer({ report, onOpenBreakdown }) {
  const [dimensionKey, setDimensionKey] = useState("product_category");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("change_amount");
  const [sortDirection, setSortDirection] = useState("desc");
  const [movement, setMovement] = useState("all");
  const tabsRef = useRef(null);
  const dimension = DIMENSIONS.find((item) => item.key === dimensionKey);
  const rows = Array.isArray(report?.breakdowns?.[dimensionKey]) ? report.breakdowns[dimensionKey] : EMPTY_ROWS;
  const hasComparison = Boolean(report?.comparison);
  const activeSort = !hasComparison && (sort === "change_amount" || sort === "change_percent") ? "sales" : sort;
  const sortedRows = useMemo(() => {
    const term = query.trim().toLocaleLowerCase();
    return rows.filter((row) => nameOf(row).toLocaleLowerCase().includes(term))
      .filter((row) => !hasComparison || movement === "all" || (movement === "gaining" ? number(row.change_amount) > 0 : number(row.change_amount) < 0))
      .sort((a, b) => {
        if (activeSort === "name") return (sortDirection === "asc" ? 1 : -1) * nameOf(a).localeCompare(nameOf(b));
        const aMissing = a[activeSort] == null;
        const bMissing = b[activeSort] == null;
        if (aMissing !== bMissing) return aMissing ? 1 : -1;
        const diff = number(a[activeSort]) - number(b[activeSort]);
        return (sortDirection === "asc" ? diff : -diff) || nameOf(a).localeCompare(nameOf(b));
      });
  }, [rows, query, activeSort, sortDirection, movement, hasComparison]);
  const chartRows = useMemo(() => mixRows(rows, hasComparison), [rows, hasComparison]);
  const hasCorrections = rows.some((row) => number(row.sales) < 0 || (hasComparison && number(row.previous_sales) < 0));
  const handleDimension = (key) => { setDimensionKey(key); setQuery(""); setMovement("all"); };
  const handleSort = (field) => {
    if (field === activeSort) {
      setSort(field);
      setSortDirection((current) => current === "asc" ? "desc" : "asc");
    } else {
      setSort(field);
      setSortDirection(field === "name" ? "asc" : "desc");
    }
  };
  useLayoutEffect(() => {
    const strip = tabsRef.current;
    const selected = strip?.querySelector('[aria-selected="true"]');
    if (!selected) return;
    const bounds = strip.getBoundingClientRect();
    const selectedBounds = selected.getBoundingClientRect();
    if (selectedBounds.left < bounds.left) strip.scrollLeft += selectedBounds.left - bounds.left;
    else if (selectedBounds.right > bounds.right) strip.scrollLeft += selectedBounds.right - bounds.right;
  }, [dimensionKey]);

  return <div className="sales-explorer">
    <div className="sales-explorer-controls">
      <span className="sales-explorer-view-label">View by</span>
      <div className="sales-explorer-tabs" role="tablist" aria-label="Sales dimension" ref={tabsRef}>
        {DIMENSIONS.map((item) => <button type="button" role="tab" aria-selected={dimensionKey === item.key} className={dimensionKey === item.key ? "is-selected" : ""} key={item.key} onClick={() => handleDimension(item.key)}>{item.label}</button>)}
      </div>
      <div className="sales-explorer-controls-right">
        <label className="sales-explorer-search"><span className="sales-explorer-visually-hidden">Search {dimension.label}</span><input type="search" aria-label={`Search ${dimension.label}`} placeholder={`Search ${dimension.search || dimension.plural}…`} value={query} onChange={(event) => setQuery(event.target.value)} /></label>
        {hasComparison && <div className="sales-explorer-movement" role="group" aria-label="Filter by movement">{["all", "gaining", "declining"].map((value) => <button type="button" key={value} aria-pressed={movement === value} className={movement === value ? "is-selected" : ""} onClick={() => setMovement(value)}>{value[0].toUpperCase() + value.slice(1)}</button>)}</div>}
      </div>
    </div>

    <section className="sales-explorer-card sales-explorer-mix" aria-label="Sales mix">
      <div className="sales-explorer-section-head"><div><h2>Sales mix by {dimension.label.toLowerCase()}</h2><p>{hasComparison ? `Compare the share of positive sales by ${dimension.label.toLowerCase()} between periods.` : `Share of positive sales by ${dimension.label.toLowerCase()} for the selected period.`}</p></div></div>
      {rows.length ? <>
        <MixBar title="Current period" dates={range(report?.date_range)} field="sales" entries={chartRows} total={report?.summary?.total_sales} />
        {hasComparison && <MixBar title="Previous period" dates={range(report.comparison)} field="previous_sales" entries={chartRows} total={report?.summary?.previous_total_sales} />}
        {chartRows.length > 0 && <div className="sales-explorer-legend">{chartRows.map((row) => <span key={row.chartKey}><i style={{ backgroundColor: row.color.background }} />{nameOf(row)}</span>)}</div>}
        {hasCorrections && <p className="sales-explorer-chart-note">The bars show positive contributions only. Signed corrections and returns remain in the table; totals are net sales.</p>}
      </> : <p className="sales-explorer-empty">No sales for this dimension in the selected period.</p>}
    </section>

    <section className="sales-explorer-card sales-explorer-breakdown" aria-label="Sales movement breakdown">
      <div className="sales-explorer-section-head"><div><h2>What moved</h2><p>{hasComparison ? `Change in sales by ${dimension.label.toLowerCase()} between ${range(report?.date_range)} and ${range(report?.comparison)}.` : `Sales by ${dimension.label.toLowerCase()} for ${range(report?.date_range)}.`}</p></div><button type="button" className="sales-explorer-open" onClick={() => onOpenBreakdown?.(dimensionKey)}>Open full breakdown <span aria-hidden="true">→</span></button></div>
      <div className="sales-explorer-table-wrap"><table className={hasComparison ? "sales-explorer-table--compared" : ""} aria-label={`${dimension.label} sales breakdown`}><thead><tr>
        <SortHeader field="name" label={dimension.label} activeSort={activeSort} sortDirection={sortDirection} onSort={handleSort} />
        <SortHeader field="sales" label="Current period" activeSort={activeSort} sortDirection={sortDirection} onSort={handleSort} />
        {hasComparison && <>
          <SortHeader field="previous_sales" label="Previous period" activeSort={activeSort} sortDirection={sortDirection} onSort={handleSort} />
          <SortHeader field="change_amount" label="Change $" activeSort={activeSort} sortDirection={sortDirection} onSort={handleSort} />
          <SortHeader field="change_percent" label="Change %" activeSort={activeSort} sortDirection={sortDirection} onSort={handleSort} />
        </>}
        <SortHeader field="share_percent" label="Share" activeSort={activeSort} sortDirection={sortDirection} onSort={handleSort} />
      </tr></thead><tbody>{sortedRows.map((row) => <tr key={keyOf(row)}><th scope="row"><span className="sales-explorer-row-name"><i className="sales-explorer-row-dot" aria-hidden="true" style={{ backgroundColor: chartRows.find((entry) => entry.chartKey === keyOf(row))?.color.background ?? OTHER_COLOR.background }} /><span>{nameOf(row)}</span></span></th><td data-label="Current">{money(row.sales)}</td>{hasComparison && <><td data-label="Previous">{money(row.previous_sales)}</td><td data-label="Change $"><span className={`sales-explorer-delta sales-explorer-delta--${toneOf(row.change_amount)}`}>{number(row.change_amount) > 0 ? "↑ " : number(row.change_amount) < 0 ? "↓ " : ""}{money(row.change_amount, true)}</span></td><td data-label="Change %"><span className={`sales-explorer-delta sales-explorer-delta--${toneOf(row.change_amount)}`}>{percent(row.change_percent)}</span></td></>}<td data-label="Share">{share(row.share_percent)}</td></tr>)}</tbody></table>{!sortedRows.length && <p className="sales-explorer-empty">{rows.length ? "No matching entries" : "No sales in this period"}</p>}</div>
    </section>
  </div>;
}
